import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import { queryKeys } from '../queryKeys';
import type { WarehouseRole } from '@/lib/permissions';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface RolePermissionRow {
  id: string;
  roleId: string;
  resource: string;
  action: string;
  allowed: boolean;
}

export interface Role {
  id: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
  permissions: RolePermissionRow[];
}

export interface RoutePermissionEntry {
  path: string;
  allowedRoles: WarehouseRole[] | 'ADMIN_ONLY' | 'ANY_AUTHENTICATED';
}

export interface ActionPermissionEntry {
  action: string;
  description: string;
  allowedRoles: WarehouseRole[] | 'ADMIN_ONLY';
}

export interface RoutePermissionsResponse {
  routes: RoutePermissionEntry[];
  actions: ActionPermissionEntry[];
  hierarchy: Record<string, WarehouseRole[]>;
}

export interface CreateRoleDto {
  name: string;
  description?: string;
  copyPermissionsFromRoleId?: string;
}

export interface PermissionDiffEntry {
  resource: string;
  action: string;
  allowed?: boolean;
}

export interface PatchRoleDto {
  name?: string;
  description?: string;
  permissionsDiff?: {
    add?: PermissionDiffEntry[];
    remove?: Array<{ resource: string; action: string }>;
    update?: PermissionDiffEntry[];
  };
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

/**
 * Fetch all roles with their permission rows.
 * Admin-only endpoint.
 */
export function useRoles() {
  return useQuery({
    queryKey: queryKeys.roles.list(),
    queryFn: async () => {
      const { data } = await apiClient.get<Role[]>('/api/roles');
      return data;
    },
  });
}

/**
 * Fetch a single role by ID with permission rows.
 * Admin-only endpoint.
 */
export function useRole(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.roles.detail(id),
    enabled: !!id,
    queryFn: async () => {
      const { data } = await apiClient.get<Role>(`/api/roles/${id}`);
      return data;
    },
  });
}

/**
 * Fetch compiled route + action permissions from the DB.
 * No auth gate — any authenticated user can call this.
 * Used by RoleProtectedRoute as API-backed replacement for hardcoded arrays.
 */
export function useRoutePermissions() {
  return useQuery({
    queryKey: queryKeys.roles.routePermissions,
    queryFn: async () => {
      const { data } = await apiClient.get<RoutePermissionsResponse>(
        '/api/roles/route-permissions',
      );
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 min (server caches for 60s)
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Create a new custom role.
 */
export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: CreateRoleDto) => {
      const { data } = await apiClient.post<Role>('/api/roles', dto);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.roles.all });
    },
  });
}

/**
 * Update role metadata and/or apply a permission diff atomically.
 */
export function usePatchRole(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: PatchRoleDto) => {
      const { data } = await apiClient.patch<Role>(`/api/roles/${id}`, dto);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.roles.all });
      // Invalidate compiled permissions so UI picks up changes immediately.
      void qc.invalidateQueries({
        queryKey: queryKeys.roles.routePermissions,
      });
    },
  });
}

/**
 * Delete a custom role. System roles return 400.
 */
export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/roles/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.roles.all });
    },
  });
}
